import { MouseEvent, useCallback, useMemo } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { Skeleton, SxProps } from '@mui/material'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { getUserAvatarText, getUserFirstName, useUser } from '$logic/users'
import { getImageUrl, getUserImageUrl } from '$func/imageUrl'
import { stringToColor } from '$func/string-to-color'
import { showImageViewerModal } from '$comp/modals/image-viewer'
import { SelectableAvatar, SelectableAvatarProps } from '$common/avatar'
import { useUserAvatarStories } from '$logic/story/react-hooks'
import { StoryAvatarBadge } from '$comp/_app/components/story/story-avatar-badge'
import { showCreateStoryModal } from '$comp/modals/create-story/create-story.api'
import { OpenStorySource, StoryAnalyticsApi } from '$logic/story/analytics'
import { openStory } from '$logic/story/service'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { showRoomProfileMenu } from '$comp/modals/menus/room-profile-menu'
import { AVATAR_SIZES } from '../avatar-base/conf'

interface UserAvatarProps
  extends Omit<
    SelectableAvatarProps,
    | 'src'
    | 'avatarBackgroundText'
    | 'storyCount'
    | 'seenStoryCount'
    | 'storyLoading'
  > {
  userId: string
  overrideImage?: string
  openImageViewer?: boolean
  withStory?: boolean
  canClientUserCreateStory?: boolean
  hideAddStoryBadge?: boolean
  disableClick?: boolean
  source?: OpenStorySource
  isStoryRibbon?: boolean
  sx?: SxProps
  dataTestId?: string
}

export function UserAvatar({
  userId,
  size = AvatarSize.SM,
  onClick,
  disableClick = false,
  overrideImage,
  openImageViewer = false,
  withStory = false,
  canClientUserCreateStory = false,
  hideAddStoryBadge,
  checked,
  source,
  isStoryRibbon,
  dataTestId,
  sx,
  ...rest
}: UserAvatarProps) {
  const { user, err, loading } = useUser(userId)

  const useAvatarStoriesHookArgs = useMemo(() => {
    return {
      peerId: userId,
      isAvatarWithStory: withStory,
      canClientUserCreateStory,
    }
  }, [userId, withStory, canClientUserCreateStory])

  const {
    storyCount,
    seenStoryCount,
    showCreateStoryBadge,
    uploading: uploadingStory,
  } = useConditionalHook(!!userId, {
    hook: useUserAvatarStories,
    hookArgs: useAvatarStoriesHookArgs,
    defaultValue: undefined,
  }) || {}

  const backgroundColor = stringToColor(getUserFirstName(user), user?.id)
  const hasDeleted = user?.deletedAt
  const src =
    overrideImage !== undefined
      ? getImageUrl(overrideImage)
      : getUserImageUrl(user)

  const openImageViewerHandler = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (onClick) {
        onClick(e)
        return
      }
      if (src) showImageViewerModal({ media: { src } })
    },
    [src, onClick],
  )

  const handleStoryClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (checked) return
      if (canClientUserCreateStory && showCreateStoryBadge) {
        e?.stopPropagation()
        showCreateStoryModal({
          peerId: userId,
          peerType: 'USER',
        })
        return
      }
      if (storyCount) {
        e?.stopPropagation()
        StoryAnalyticsApi.open(
          userId,
          'USER',
          source || OpenStorySource.UNKNOWN,
        )

        const elem = (e?.target as HTMLDivElement)?.getBoundingClientRect()

        openStory({
          peerId: userId,
          peerType: 'USER',
          xAxis: elem?.x,
          yAxis: elem?.y,
          singleMode: !isStoryRibbon,
        })
      }
    },
    [
      userId,
      checked,
      canClientUserCreateStory,
      storyCount,
      showCreateStoryBadge,
    ],
  )

  const onStoryItemClick = useCallback(
    async (e: MouseEvent<HTMLDivElement>) => {
      await closeCurrentModal()
      handleStoryClick(e)
    },
    [handleStoryClick],
  )

  const onProfileItemClick = useCallback(
    async (e: MouseEvent<HTMLDivElement>) => {
      await closeCurrentModal()
      openImageViewerHandler(e)
    },
    [openImageViewerHandler],
  )

  const hasStory = storyCount > 0
  const handleAvatarInRoomProfileClick = useCallback(
    (e: any) => {
      // NOTE: Click on avatar scenarios are written in Notion documentations
      if (hasStory && src) {
        showRoomProfileMenu({
          hasStory,
          xAxis: e.clientX,
          yAxis: e.clientY,
          onStoryClick: onStoryItemClick,
          onProfileClick: onProfileItemClick,
        })
      } else if (!hasStory && src) {
        openImageViewerHandler(e)
      } else if (hasStory && !src) {
        handleStoryClick(e)
      }
    },
    [
      hasStory,
      src,
      handleStoryClick,
      onProfileItemClick,
      onStoryItemClick,
      openImageViewerHandler,
    ],
  )

  const handleClick = useMemo(() => {
    if (disableClick) return undefined
    if (source === OpenStorySource.ROOM_PROFILE)
      return handleAvatarInRoomProfileClick

    return (
      onClick ||
      (withStory
        ? handleStoryClick
        : openImageViewer
          ? openImageViewerHandler
          : undefined)
    )
  }, [
    handleAvatarInRoomProfileClick,
    withStory,
    onClick,
    openImageViewer,
    openImageViewerHandler,
    handleStoryClick,
    disableClick,
    source,
  ])

  if (loading || err)
    return (
      <Skeleton
        variant="circular"
        width={AVATAR_SIZES[size]}
        height={AVATAR_SIZES[size]}
      />
    )

  return (
    <StoryAvatarBadge
      onClick={handleClick}
      invisible={!showCreateStoryBadge || hideAddStoryBadge}
      sx={sx}
      dataTestId={dataTestId}
    >
      <SelectableAvatar
        storyLoading={uploadingStory}
        checked={checked}
        storyCount={storyCount}
        seenStoryCount={seenStoryCount}
        avatarBackgroundText={getUserAvatarText(user)}
        src={src}
        size={size}
        backgroundColor={backgroundColor}
        hasDeleted={hasDeleted}
        {...rest}
      />
    </StoryAvatarBadge>
  )
}
